import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface StaffMember {
  id: string;
  full_name: string;
  role_name: string;
}

interface DailyRow {
  date: string;
  work_seconds: number;
  break_seconds: number;
  shifts: number;
  has_correction: boolean;
}

interface MonthlySummary {
  total_work_seconds: number;
  total_break_seconds: number;
  shift_count: number;
  total_work_days: number;
  reference_pay_yen: number | null;
  employment_type: string | null;
  hourly_wage_yen: number | null;
  monthly_salary_yen: number | null;
}

interface CorrectionForm {
  targetEventId: string;
  newTime: string;
  newType: string;
  reason: string;
}

export default function AttendanceAdminPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [staffList, setStaffList] = useState<StaffMember[]>([]);
  const [selectedStaffId, setSelectedStaffId] = useState<string>('');
  const [dailyRows, setDailyRows] = useState<DailyRow[]>([]);
  const [monthlySummary, setMonthlySummary] = useState<MonthlySummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Correction state
  const [showCorrection, setShowCorrection] = useState(false);
  const [correctionForm, setCorrectionForm] = useState<CorrectionForm>({
    targetEventId: '',
    newTime: '',
    newType: 'clock_in',
    reason: '',
  });
  const [correcting, setCorrecting] = useState(false);
  const [correctionError, setCorrectionError] = useState<string | null>(null);
  const [correctionSuccess, setCorrectionSuccess] = useState<string | null>(null);

  // Fetch staff list
  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from('profiles')
          .select('id, full_name, staff_roles!inner(role_name)')
          .eq('is_active', true)
          .order('full_name', { ascending: true });

        if (fetchError) throw fetchError;

        const mapped: StaffMember[] = (data || []).map((item: Record<string, unknown>) => {
          const roleData = (item.staff_roles as Record<string, unknown>) || {};
          return {
            id: item.id as string,
            full_name: item.full_name as string,
            role_name: roleData.role_name as string,
          };
        });
        setStaffList(mapped);
      } catch {
        setError('スタッフ一覧の取得に失敗しました');
      }
    };
    fetchStaff();
  }, []);

  // Fetch attendance data when staff/month changes
  const fetchData = useCallback(async () => {
    if (!selectedStaffId) return;
    setLoading(true);
    setError(null);
    setDailyRows([]);
    setMonthlySummary(null);

    try {
      const yearStr = String(year);
      const monthStr = String(month).padStart(2, '0');

      // Daily timesheet for the full month
      const fromDate = `${yearStr}-${monthStr}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const toDate = `${yearStr}-${monthStr}-${String(lastDay).padStart(2, '0')}`;

      const { data: dailyData, error: dailyError } = await supabase
        .rpc('compute_daily_timesheet', {
          p_staff_id: selectedStaffId,
          p_from_date: fromDate,
          p_to_date: toDate,
        });

      if (dailyError) throw dailyError;

      const rows: DailyRow[] = [];
      // Check corrections by querying time_clock_events
      const { data: correctionEvents, error: corrError } = await supabase
        .from('time_clock_events')
        .select('id, event_time, event_type, original_event_id, correction_type')
        .eq('staff_id', selectedStaffId)
        .gte('event_time', fromDate)
        .lte('event_time', `${toDate}T23:59:59`)
        .not('correction_type', 'is', null);

      if (corrError) throw corrError;

      const correctedDates = new Set<string>();
      (correctionEvents || []).forEach((ev: Record<string, unknown>) => {
        const d = (ev.event_time as string).split('T')[0];
        correctedDates.add(d);
      });

      const dailyMap = new Map<string, DailyRow>();
      if (dailyData) {
        (dailyData as Array<Record<string, unknown>>).forEach((row) => {
          dailyMap.set(row.work_date as string, {
            date: row.work_date as string,
            work_seconds: row.total_work_seconds as number,
            break_seconds: row.total_break_seconds as number,
            shifts: row.shift_count as number,
            has_correction: false,
          });
        });
      }

      for (let d = 1; d <= lastDay; d++) {
        const dateStr = `${yearStr}-${monthStr}-${String(d).padStart(2, '0')}`;
        const existing = dailyMap.get(dateStr);
        rows.push({
          date: dateStr,
          work_seconds: existing?.work_seconds || 0,
          break_seconds: existing?.break_seconds || 0,
          shifts: existing?.shifts || 0,
          has_correction: correctedDates.has(dateStr),
        });
      }

      setDailyRows(rows);

      // Monthly timesheet
      const { data: monthlyData, error: monthlyError } = await supabase
        .rpc('compute_monthly_timesheet', {
          p_staff_id: selectedStaffId,
          p_year: year,
          p_month: month,
        });

      if (!monthlyError && monthlyData && monthlyData.length > 0) {
        const m = monthlyData[0] as Record<string, unknown>;
        setMonthlySummary({
          total_work_seconds: m.total_work_seconds as number,
          total_break_seconds: m.total_break_seconds as number,
          shift_count: m.shift_count as number,
          total_work_days: m.total_work_days as number,
          reference_pay_yen: m.reference_pay_yen as number | null,
          employment_type: m.employment_type as string | null,
          hourly_wage_yen: m.hourly_wage_yen as number | null,
          monthly_salary_yen: m.monthly_salary_yen as number | null,
        });
      } else {
        setMonthlySummary({
          total_work_seconds: 0, total_break_seconds: 0, shift_count: 0,
          total_work_days: 0, reference_pay_yen: null,
          employment_type: null, hourly_wage_yen: null, monthly_salary_yen: null,
        });
      }
    } catch {
      setError('勤怠データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [selectedStaffId, year, month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCorrect = async () => {
    if (!selectedStaffId || !correctionForm.targetEventId || !correctionForm.reason.trim()) {
      setCorrectionError('すべての項目を入力してください');
      return;
    }

    setCorrecting(true);
    setCorrectionError(null);
    setCorrectionSuccess(null);

    try {
      const newEventTime = new Date(correctionForm.newTime).toISOString();

      const { error: rpcError } = await supabase
        .rpc('admin_correct_time_clock', {
          p_correction_type: 'replace',
          p_original_event_id: correctionForm.targetEventId,
          p_corrected_event_time: newEventTime,
          p_event_type: correctionForm.newType,
          p_staff_id: selectedStaffId,
          p_correction_reason: correctionForm.reason.trim(),
        });

      if (rpcError) {
        setCorrectionError('訂正に失敗しました');
        setCorrecting(false);
        return;
      }

      setCorrectionSuccess('訂正が完了しました');
      setShowCorrection(false);
      fetchData();
    } catch {
      setCorrectionError('訂正に失敗しました');
    } finally {
      setCorrecting(false);
    }
  };

  const formatMinutes = (seconds: number) => {
    const totalMinutes = Math.floor(seconds / 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h}時間${m}分`;
  };

  const formatYen = (amount: number | null | undefined) => {
    if (amount == null) return '--';
    return `¥${amount.toLocaleString()}`;
  };

  const referencePay = monthlySummary?.reference_pay_yen;

  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - 2 + i);

  return (
    <div className="max-w-7xl">
      {/* Page Header */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-foreground-900 font-heading">勤怠管理（管理者）</h2>
        <p className="text-sm text-foreground-600 mt-1">スタッフの勤務状況確認と訂正</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3 mb-6">
        <div>
          <label className="block text-sm font-medium text-foreground-800 mb-1">スタッフ</label>
          <select
            value={selectedStaffId}
            onChange={(e) => setSelectedStaffId(e.target.value)}
            className="px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300 cursor-pointer min-w-[200px]"
          >
            <option value="">選択してください</option>
            {staffList.map((s) => (
              <option key={s.id} value={s.id}>{s.full_name}（{s.role_name === 'admin' ? '管理者' : s.role_name === 'front_desk' ? 'フロント' : '清掃'}）</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-800 mb-1">年</label>
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10))}
            className="px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300 cursor-pointer"
          >
            {years.map((y) => (<option key={y} value={y}>{y}年</option>))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-800 mb-1">月</label>
          <select
            value={month}
            onChange={(e) => setMonth(parseInt(e.target.value, 10))}
            className="px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300 cursor-pointer"
          >
            {months.map((m) => (<option key={m} value={m}>{m}月</option>))}
          </select>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 flex items-center gap-2.5 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-error-warning-line text-sm"></i>
          </span>
          {error}
        </div>
      )}

      {/* No selection */}
      {!selectedStaffId && !loading && (
        <div className="bg-background-50 rounded-xl border border-background-200/70 p-12 text-center">
          <span className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
            <i className="ri-user-search-line text-3xl text-foreground-400"></i>
          </span>
          <p className="text-sm text-foreground-600">スタッフを選択してください</p>
        </div>
      )}

      {/* Monthly Summary */}
      {selectedStaffId && monthlySummary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-background-50 rounded-xl border border-background-200/70 p-5">
            <p className="text-xs text-foreground-600 mb-1">月間勤務時間</p>
            <p className="text-xl font-bold text-foreground-900 font-heading">{formatMinutes(monthlySummary.total_work_seconds)}</p>
          </div>
          <div className="bg-background-50 rounded-xl border border-background-200/70 p-5">
            <p className="text-xs text-foreground-600 mb-1">月間休憩時間</p>
            <p className="text-xl font-bold text-foreground-900 font-heading">{formatMinutes(monthlySummary.total_break_seconds)}</p>
          </div>
          <div className="bg-background-50 rounded-xl border border-background-200/70 p-5">
            <p className="text-xs text-foreground-600 mb-1">
              {monthlySummary.employment_type === 'hourly' ? '参考給与額（時給制）' : '月給額'}
            </p>
            <p className="text-xl font-bold text-foreground-900 font-heading">
              {monthlySummary.employment_type === 'hourly'
                ? formatYen(referencePay)
                : formatYen(monthlySummary.monthly_salary_yen)}
            </p>
            <p className="text-xs text-foreground-500 mt-1">確定給与ではなく給与計算の参考資料</p>
          </div>
        </div>
      )}

      {/* Daily Table */}
      {selectedStaffId && (
        <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden mb-6">
          <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground-900">日別勤務時間</h3>
            <button
              onClick={() => { setShowCorrection(true); setCorrectionError(null); setCorrectionSuccess(null); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-secondary-100/80 hover:bg-secondary-100 text-secondary-800 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-edit-line"></i>
              </span>
              訂正
            </button>
          </div>
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-loader-4-line animate-spin text-primary-500"></i>
                </span>
              </div>
            ) : dailyRows.length === 0 ? (
              <div className="text-center py-8 text-sm text-foreground-500">データがありません</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200/70 bg-background-100">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider">日付</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider">曜日</th>
                    <th className="text-right px-5 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider">勤務時間</th>
                    <th className="text-right px-5 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider">休憩時間</th>
                    <th className="text-right px-5 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider">シフト数</th>
                    <th className="text-center px-5 py-3 text-xs font-semibold text-foreground-600 uppercase tracking-wider">訂正</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyRows.map((row) => {
                    const d = new Date(row.date);
                    const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
                    const isSunday = d.getDay() === 0;
                    return (
                      <tr key={row.date} className={`border-b border-background-200/70 hover:bg-background-100 transition-colors ${isSunday ? 'bg-red-50/30' : ''}`}>
                        <td className="px-5 py-3 text-foreground-900 font-medium">
                          {d.getDate()}日
                        </td>
                        <td className={`px-5 py-3 ${isSunday ? 'text-red-600' : 'text-foreground-700'}`}>
                          {weekdays[d.getDay()]}
                        </td>
                        <td className="px-5 py-3 text-right text-foreground-900 font-medium">
                          {row.work_seconds > 0 ? formatMinutes(row.work_seconds) : '--'}
                        </td>
                        <td className="px-5 py-3 text-right text-foreground-700">
                          {row.break_seconds > 0 ? formatMinutes(row.break_seconds) : '--'}
                        </td>
                        <td className="px-5 py-3 text-right text-foreground-700">
                          {row.shifts > 0 ? `${row.shifts}回` : '--'}
                        </td>
                        <td className="px-5 py-3 text-center">
                          {row.has_correction ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-800">有</span>
                          ) : (
                            <span className="text-xs text-foreground-400">--</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Correction Modal */}
      {showCorrection && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="relative bg-background-50 rounded-xl border border-background-200/70 w-full max-w-md mx-4">
            <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground-900">打刻訂正</h3>
              <button
                onClick={() => setShowCorrection(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-close-line text-foreground-700"></i>
                </span>
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-xs text-foreground-500 bg-secondary-50 rounded-lg px-3 py-2 border border-background-200/60">
                元イベントの更新・削除はできません。訂正レコードが新規作成されます。
              </p>

              {correctionError && (
                <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-error-warning-line text-sm"></i>
                  </span>
                  {correctionError}
                </div>
              )}
              {correctionSuccess && (
                <div className="flex items-center gap-2 rounded-lg bg-accent-100 px-3 py-2 text-sm text-accent-800">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-check-line text-sm"></i>
                  </span>
                  {correctionSuccess}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1">対象イベントID <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={correctionForm.targetEventId}
                  onChange={(e) => setCorrectionForm((prev) => ({ ...prev, targetEventId: e.target.value }))}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300"
                  placeholder="UUID of the original event"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1">新しい打刻時刻 <span className="text-red-500">*</span></label>
                <input
                  type="datetime-local"
                  value={correctionForm.newTime}
                  onChange={(e) => setCorrectionForm((prev) => ({ ...prev, newTime: e.target.value }))}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300 cursor-pointer"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1">打刻種類 <span className="text-red-500">*</span></label>
                <select
                  value={correctionForm.newType}
                  onChange={(e) => setCorrectionForm((prev) => ({ ...prev, newType: e.target.value }))}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300 cursor-pointer"
                >
                  <option value="clock_in">出勤</option>
                  <option value="break_start">休憩開始</option>
                  <option value="break_end">休憩終了</option>
                  <option value="clock_out">退勤</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-800 mb-1">訂正理由 <span className="text-red-500">*</span></label>
                <textarea
                  value={correctionForm.reason}
                  onChange={(e) => setCorrectionForm((prev) => ({ ...prev, reason: e.target.value }))}
                  maxLength={500}
                  rows={3}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300 resize-none"
                  placeholder="訂正の理由を入力"
                ></textarea>
                <p className="text-xs text-foreground-500 mt-1 text-right">{correctionForm.reason.length}/500</p>
              </div>
              <div className="flex items-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCorrection(false)}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  キャンセル
                </button>
                <button
                  onClick={handleCorrect}
                  disabled={correcting}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {correcting ? (
                    <>
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin"></i>
                      </span>
                      処理中...
                    </>
                  ) : (
                    '訂正を実行'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
